import { Controller } from "stimulus";

export default class extends Controller {
  static targets = ["input", "chart"];

  connect() {
    // Load filter from localStorage on connect
    this.loadFilterFromStorage();
    // Load show changes preference from localStorage
    this.loadShowChangesPreference();
  }

  get storageKey() {
    // Use portal path only (without report ID or preview) so filter is shared across all reports for the portal
    const pathname = window.location.pathname;
    // Match both /reports/ID/... and /reports/preview patterns and normalize to base portal path
    const portalPath = pathname.replace(/\/reports\/((\d+.*)|preview.*)$/, "");

    return `chart_filter_${portalPath}`;
  }

  get showChangesStorageKey() {
    return "show_chart_changes";
  }

  loadFilterFromStorage() {
    // Load filter from localStorage
    const filter = localStorage.getItem(this.storageKey);

    if (filter && this.hasInputTarget) {
      this.inputTarget.value = filter;
      // Apply filter with a small delay to ensure charts are rendered
      setTimeout(() => {
        this.applyFilter();
      }, 200);
    }
  }

  saveFilterToStorage(filterValue) {
    if (filterValue) {
      localStorage.setItem(this.storageKey, filterValue);
    } else {
      localStorage.removeItem(this.storageKey);
    }
  }

  filter() {
    // Debounce the filter function to avoid too many updates
    if (this.filterTimeout) {
      clearTimeout(this.filterTimeout);
    }

    this.filterTimeout = setTimeout(() => {
      const filterValue = this.inputTarget.value.trim();

      this.saveFilterToStorage(filterValue);
      this.applyFilter();
    }, 300);
  }

  applyFilter() {
    const filterValue = this.inputTarget.value.trim();

    // If no filter, show all charts
    if (!filterValue) {
      this.chartTargets.forEach(chart => {
        chart.style.display = "";
      });
      return;
    }

    // Try to compile the regex pattern
    let regex;

    try {
      regex = new RegExp(filterValue, "i"); // case-insensitive by default
    } catch (e) {
      // Invalid regex, treat as literal string
      const escapedFilter = filterValue.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

      regex = new RegExp(escapedFilter, "i");
    }

    // Filter charts based on their title
    this.chartTargets.forEach(chart => {
      const title = this.getChartTitle(chart);
      const shouldShow = title && regex.test(title);

      chart.style.display = shouldShow ? "" : "none";
    });
  }

  getChartTitle(chartElement) {
    // Look for title in various possible locations
    // First, look for any element with a data attribute that ends with -target="title"
    // This catches all Stimulus target elements for titles
    const allElements = Array.from(chartElement.querySelectorAll("*"));

    const titleElement = allElements.find(element =>
      Array.from(element.attributes).some(
        attr => attr.name.includes("-target") && attr.value === "title",
      ),
    );

    if (titleElement) {
      return titleElement.textContent.trim();
    }

    // Check for h2, h3, h4 elements that might contain the title
    const headerElement = chartElement.querySelector("h2, h3, h4");

    if (headerElement) {
      return headerElement.textContent;
    }

    // Check for any element with class that might indicate a title
    const classTitle = chartElement.querySelector(".chart-title, .title");

    if (classTitle) {
      return classTitle.textContent;
    }

    // Try to find Chart.js canvas and extract title from the chart instance
    const canvas = chartElement.querySelector("canvas");

    // eslint-disable-next-line no-underscore-dangle
    if (canvas && canvas._chart) {
      // Chart.js 2.x stores chart instance on canvas element
      // eslint-disable-next-line no-underscore-dangle
      const chartInstance = canvas._chart;

      if (chartInstance.options?.plugins?.title?.text) {
        return chartInstance.options.plugins.title.text;
      }
      if (chartInstance.options?.title?.text) {
        return chartInstance.options.title.text;
      }
    }

    // For Chart.js 3.x, check if chart is stored differently
    if (canvas && window.Chart) {
      const chartInstance = window.Chart.getChart(canvas);

      if (chartInstance?.options?.plugins?.title?.text) {
        return chartInstance.options.plugins.title.text;
      }
    }

    // Try to extract from the Stimulus controller instance
    const chartController = chartElement.querySelector("[data-controller*='chart']");

    if (chartController) {
      // Get the Stimulus application and controller
      const controllerName = chartController
        .getAttribute("data-controller")
        .split(" ")
        .find(name => name.includes("chart"));

      if (controllerName && window.Stimulus) {
        try {
          // Try to get the controller instance from the element
          const app = window.Stimulus || this.application;
          const controller = app.getControllerForElementAndIdentifier(
            chartController,
            controllerName,
          );

          if (controller) {
            // Check for various title properties/methods
            if (controller.titleText) {
              return controller.titleText;
            }
            if (controller.titleTarget && controller.titleTarget.textContent) {
              return controller.titleTarget.textContent;
            }
            if (controller.chart?.options?.plugins?.title?.text) {
              return controller.chart.options.plugins.title.text;
            }
          }
        } catch (e) {
          // Silently fail if we can't access the controller
        }
      }

      // Last resort: return controller name as a hint
      return controllerName;
    }

    return "";
  }

  clear() {
    this.inputTarget.value = "";
    this.saveFilterToStorage("");
    this.applyFilter();
  }

  loadShowChangesPreference() {
    // Load show changes preference from localStorage (default: false)
    const showChanges = localStorage.getItem(this.showChangesStorageKey) === "true";
    const checkbox = document.getElementById("show-chart-changes");

    if (checkbox) {
      checkbox.checked = showChanges;
      this.updateDeltaVisibility(showChanges);
    }
  }

  toggleShowChanges(event) {
    const showChanges = event.target.checked;

    // Save preference to localStorage
    localStorage.setItem(this.showChangesStorageKey, showChanges.toString());
    // Update delta visibility
    this.updateDeltaVisibility(showChanges);
  }

  updateDeltaVisibility(showChanges) {
    // Find all delta indicators and toggle their visibility
    const deltaIndicators = document.querySelectorAll("[data-chart-toggle-target='delta']");

    deltaIndicators.forEach(indicator => {
      indicator.style.display = showChanges ? "" : "none";
    });
  }
}
